import { format as d3Format } from 'd3-format';
import type { GeoJsonProperties } from 'geojson';
import isNil from 'lodash/isNil';
import { useQuery } from 'urql';

import {
  COMPOSITION_V3_TOOLTIP,
  type CompositionV2Entity,
  type CompositionV2Filters,
  type CompositionV2FullSnapshotReq,
  type CompositionV3Req,
  EntKind,
  POSTING_SOURCE_IDS,
  POSTINGS_V3_TOOLTIP,
  PositionsEntityKind,
  type PostingFilters,
  type PostingsV3Filters,
  PostingsV3Mode,
  type PostingsV3TooltipQuery,
  type PostingsV3TooltipQueryVariables,
} from '@revelio/data-access';
import { formatterWithExchangeRate } from '@revelio/replots';

import { TooltipFeatureType } from '../../next/CustomDash/components/Chart/Map/utils/tooltip-feature-type';
import { usePayData } from '../../next/CustomDash/components/DataProvider/compensation/use-pay-data';
import { calculateMarketTightnessMeterValue } from '../../next/CustomDash/components/DataProvider/market-conditions/z-score/calculate-meter-value';
import {
  DataProviderQueryStatus,
  type DistributionData,
} from '../../next/CustomDash/components/DataProvider/types';
import { getDistributionDataFromPay } from '../../next/CustomDash/components/DataProvider/utils/convert-compensation-data';
import { useCompositionV3QueryWithStatus } from '../../next/CustomDash/components/DataResolver/use-composition-v3-query';
import {
  DEFAULT_ACTIVITY_TAXONOMY_ID,
  DEFAULT_SKILL_TAXONOMY_ID,
} from '../../next/CustomDash/filters/contexts/defaultFilters';
import {
  type Filter,
  FilterId,
  type PrimaryFilterId,
} from '../../next/CustomDash/filters/types/filters';
import {
  getCompositionV3BreakdownOptions,
  roleCountsLevelFromCompositionFilters,
} from '../../next/CustomDash/filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  mapCompositionV2FilterSelectionsToV3,
  mapTooltipFiltersToCompositionV3Corrections,
  mapTooltipFiltersToCompositionV3Filters,
} from '../../next/CustomDash/filters/utils/composition-v3/map-tooltip-filters-to-v3';
import { toPostingsCompatibleFilters } from '../../next/CustomDash/filters/utils/getPostingsV3Variables';
import {
  mapPositionsEntitiesToCompositionV3,
  stripEntitiesFromFilters,
} from '../../next/CustomDash/filters/utils/role-skill-ent-kind';
import { useMonthApiFilters } from '../../next/entity-summary/use-month-api-filters';
import { useCurrencyPreference } from '../user-preferences/hooks/useCurrencyPreference';
import { mapRoleTaxonomySelectionToId } from '../user-preferences/utils/taxonomy-mapper';
import {
  getTooltipV3FilterSet,
  replaceGeographyFilters,
} from './get-tooltip-v3-variables';

export interface TooltipData {
  header: string;
  headcount?: string;
  growthRate?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showSalaryData?: boolean;
}

export interface UseFetchTooltipDataParams {
  primaryDimension: PrimaryFilterId;
  tooltipFeature: GeoJsonProperties | null;
  compositionEntities?: CompositionV2Entity[];
  compositionFilters?: CompositionV2Filters;
  postingFilters?: PostingFilters;
  skipComposition?: boolean;
  skipPosting: boolean; // Users who don't have Posting tab access should skip this query which will result in - postings
  skipCompensation?: boolean;
  /**
   * The page's effective filters. Feeds the native v3 derivation. Optional so
   * callers that have not migrated keep working — they fall back to the
   * legacy v2→v3 filter mapping and simply never get the v3-only filters
   * (activity).
   */
  effectiveFilters?: Filter[];
}

const formatInt = d3Format(',.0f');
const signedPct = d3Format('+.1%');

const formatSignedPct = (value: number): string => {
  const formatted = signedPct(value);
  if (formatted === '+0.0%' || formatted === '−0.0%') return '0%';
  return formatted;
};

export const useFetchTooltipData = ({
  primaryDimension,
  tooltipFeature,
  compositionEntities = [],
  compositionFilters,
  postingFilters = {},
  skipComposition = false,
  skipPosting = false,
  skipCompensation = false,
  effectiveFilters,
}: UseFetchTooltipDataParams): TooltipData | null => {
  const { code: currencyCode, exchangeRate } = useCurrencyPreference();
  const currencyFormatter = formatterWithExchangeRate(
    currencyCode,
    exchangeRate
  );
  const hasGranularSelection =
    hasGranularGeoSelection(compositionFilters) ||
    hasGranularGeoSelection(postingFilters);
  const geoFilter = getTooltipGeoFilter(tooltipFeature, hasGranularSelection);
  const mapTooltipEntities = buildTooltipEntities(tooltipFeature, []);
  const tooltipCountyId = getTooltipCountyId(tooltipFeature);
  // When the page's primary entity is non-geographic (e.g. a company or role
  // overview), a hovered geography scopes that entity rather than being the
  // subject itself — keep the primary entity and apply the geography as a
  // filter. Geography and td_users maps still treat the hovered geography as
  // the entity.
  const hasNonGeoPrimaryEntity =
    compositionEntities.length > 0 && primaryDimension !== FilterId.GEOGRAPHY;
  const useTooltipEntity =
    !hasNonGeoPrimaryEntity &&
    (mapTooltipEntities.length > 0 ||
      tooltipCountyId !== null ||
      (primaryDimension === FilterId.GEOGRAPHY && !isNil(geoFilter)));

  const compositionEntitiesEffective = useTooltipEntity
    ? buildTooltipEntities(tooltipFeature, compositionEntities)
    : compositionEntities;

  const compositionWithTooltipGeoFilters = replaceGeographyFilters(
    compositionFilters,
    useTooltipEntity ? {} : geoFilter
  );

  const postingGeoFilter = useTooltipEntity
    ? getTooltipGeoFilter(tooltipFeature, false)
    : geoFilter;

  const postingWithTooltipGeoFilters = replaceGeographyFilters(
    postingFilters,
    postingGeoFilter
  );

  const dateRangeApiFilters = useMonthApiFilters();

  const {
    raw: _raw,
    start_date: _start_date,
    end_date: _end_date,
    ...compositionFiltersForQuery
  } = compositionWithTooltipGeoFilters ?? {};

  const snapshotDateForComposition = dateRangeApiFilters?.date_range_api_filters
    ?.end_date as string;

  const customRoleForTaxonomy = compositionFiltersForQuery?.custom_role;
  const customSkillForTaxonomy = compositionFiltersForQuery?.custom_skill;
  // An adaptive taxonomy is encoded as `ricsK50Id` / `ricsK10Id` rather than a
  // numeric `taxonomyId`, so it has to go through the mapper the chart queries
  // use — reading `taxonomyId` directly resolves only explicit custom taxonomies.
  const role_taxonomy = customRoleForTaxonomy?.taxonomyId
    ? mapRoleTaxonomySelectionToId(customRoleForTaxonomy.taxonomyId)
    : 0;
  const skill_taxonomy =
    customSkillForTaxonomy?.taxonomy_id !== undefined &&
    customSkillForTaxonomy.taxonomy_id !== null
      ? customSkillForTaxonomy.taxonomy_id
      : DEFAULT_SKILL_TAXONOMY_ID;

  const compositionV3FiltersForTooltip =
    mapTooltipFiltersToCompositionV3Filters({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
      snapshotDate: snapshotDateForComposition,
    });

  const compositionV3EntitiesLegacy =
    tooltipCountyId !== null && useTooltipEntity
      ? [{ EntID: tooltipCountyId, EntType: EntKind.County }]
      : (mapPositionsEntitiesToCompositionV3(compositionEntitiesEffective) ??
        []);

  // Native filter/entity derivation needs the page's effective filters, so it
  // only applies for callers that pass them; the rest fall back to the legacy
  // v2→v3 filter mapping.
  const nativeV3 = effectiveFilters
    ? getTooltipV3FilterSet({
        effectiveFilters,
        primaryDimension,
        geoFilter,
        tooltipCountyId,
        useTooltipEntity,
        // `compositionEntitiesEffective` already resolves both cases: the
        // hovered feature when it is the subject, the page's entities when
        // it is only a scope. The module still special-cases a hovered
        // county, which has no v2 entity kind to be built from.
        entitiesOverride:
          mapPositionsEntitiesToCompositionV3(compositionEntitiesEffective) ??
          [],
        snapshotDate: snapshotDateForComposition,
      })
    : null;

  const compositionV3EntitiesForTooltip =
    nativeV3?.entities ?? compositionV3EntitiesLegacy;

  const compositionV3FiltersStripped =
    nativeV3?.filters ??
    stripEntitiesFromFilters(
      compositionV3FiltersForTooltip,
      compositionV3EntitiesForTooltip
    );

  const compositionV3CorrectionsForTooltip =
    nativeV3?.corrections ??
    mapTooltipFiltersToCompositionV3Corrections({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
    });

  const tooltipBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: compositionV3FiltersStripped,
  });

  const compositionTooltipReq: CompositionV3Req = {
    entities: compositionV3EntitiesForTooltip,
    filters: compositionV3FiltersStripped,
    corrections: compositionV3CorrectionsForTooltip,
    role_taxonomy: nativeV3?.role_taxonomy ?? role_taxonomy,
    skill_taxonomy: nativeV3?.skill_taxonomy ?? skill_taxonomy,
    // v3-only: the legacy v2 mapping has no activity concept to carry.
    activity_taxonomy:
      nativeV3?.activity_taxonomy ?? DEFAULT_ACTIVITY_TAXONOMY_ID,
    breakdown_options: {
      geo_breakdown_level: tooltipBreakdownOptions.geo_breakdown_level,
      rics_breakdown_level: tooltipBreakdownOptions.rics_breakdown_level,
      role_breakdown_level: tooltipBreakdownOptions.role_breakdown_level,
      skill_breakdown_level: tooltipBreakdownOptions.skill_breakdown_level,
    },
  };

  const pauseComposition =
    skipComposition ||
    !tooltipFeature ||
    compositionV3EntitiesForTooltip.length === 0;

  const [{ data: compositionTooltipData, fetching: compositionFetching }] =
    useCompositionV3QueryWithStatus({
      query: COMPOSITION_V3_TOOLTIP,
      variables: { req: compositionTooltipReq },
      pause: pauseComposition,
    });
  const compositionTooltipEntity =
    compositionTooltipData?.composition_v3?.entities?.[0];

  // Weekly date range the posting tooltip reads its latest value from. Prefer the
  // page's posting date filter (a selected range, or the overview snapshot's
  // 2-week window) so the tooltip reflects the selected date, not always the
  // latest week; fall back to the full available range. postings_v3 expects
  // weekly Monday-aligned YYYY-MM-DD dates, which both sources provide.
  const postingStartDate =
    postingWithTooltipGeoFilters?.start_date ??
    dateRangeApiFilters?.date_range_full_api_filters?.start_date;
  const postingEndDate =
    postingWithTooltipGeoFilters?.end_date ??
    dateRangeApiFilters?.date_range_full_api_filters?.end_date;

  // The hovered geo / primary entity is the subject, so reuse the composition
  // tooltip's v3 entities and its v2->v3 mapping, swapping in the posting-
  // specific geo, then strip the entity dims. The composition mapping keeps
  // demographics and keywords (valid for people-based composition but not for
  // postings, which are job ads), so run the result through
  // toPostingsCompatibleFilters to drop them before overlaying provider +
  // weekly dates.
  const postingV3FilterSelections = replaceGeographyFilters(
    // Native derivation when available so v3-only filters (activity) survive;
    // the postings geo still overrides the composition one either way.
    nativeV3?.filters ??
      mapCompositionV2FilterSelectionsToV3(
        compositionFiltersForQuery as Partial<CompositionV2Filters>
      ),
    postingGeoFilter
  );
  const postingV3FiltersStripped = stripEntitiesFromFilters(
    postingV3FilterSelections,
    compositionV3EntitiesForTooltip
  );

  const postingV3Filters: PostingsV3Filters = {
    ...toPostingsCompatibleFilters(postingV3FiltersStripped),
    provider: [POSTING_SOURCE_IDS.unified],
    start_date: postingStartDate,
    end_date: postingEndDate,
  };

  const postingTooltipVariables: PostingsV3TooltipQueryVariables = {
    req: {
      entities: compositionV3EntitiesForTooltip,
      filters: postingV3Filters,
      // Tooltip uses the `expected_hires` metric mode.
      options: {
        mode: PostingsV3Mode.Hires,
        roles_counts_level: roleCountsLevelFromCompositionFilters(
          postingV3FiltersStripped
        ),
      },
      role_taxonomy: nativeV3?.role_taxonomy ?? role_taxonomy,
      skill_taxonomy: nativeV3?.skill_taxonomy ?? skill_taxonomy,
      activity_taxonomy:
        nativeV3?.activity_taxonomy ?? DEFAULT_ACTIVITY_TAXONOMY_ID,
    },
  };

  const [{ data: postingDataV3, fetching: postingV3Fetching }] = useQuery<
    PostingsV3TooltipQuery,
    PostingsV3TooltipQueryVariables
  >({
    query: POSTINGS_V3_TOOLTIP,
    variables: postingTooltipVariables,
    pause: skipPosting || !tooltipFeature,
  });

  // The pay tooltip reuses the composition tooltip's filter set but keeps the
  // pay request's own date range (the composition mapping would overwrite
  // dates with the snapshot month).
  const payTooltipFilters = nativeV3
    ? {
        ...nativeV3.filters,
        // The derived set pins one snapshot month for composition; pay reads a
        // distribution across the page's range, so restore its own dates.
        start_date: compositionWithTooltipGeoFilters?.start_date,
        end_date: compositionWithTooltipGeoFilters?.end_date,
      }
    : stripEntitiesFromFilters(
        {
          ...mapCompositionV2FilterSelectionsToV3(
            compositionWithTooltipGeoFilters as Partial<CompositionV2Filters>
          ),
          start_date: compositionWithTooltipGeoFilters?.start_date,
          end_date: compositionWithTooltipGeoFilters?.end_date,
        },
        compositionV3EntitiesForTooltip
      );

  const { data: compensationData, status: compensationStatus } = usePayData({
    variant: 'tooltip',
    req: {
      entities: compositionV3EntitiesForTooltip,
      filters: payTooltipFilters,
      corrections:
        nativeV3?.corrections ??
        mapTooltipFiltersToCompositionV3Corrections({
          filters:
            compositionWithTooltipGeoFilters as Partial<CompositionV2Filters>,
        }),
      role_taxonomy: nativeV3?.role_taxonomy ?? role_taxonomy,
      skill_taxonomy: nativeV3?.skill_taxonomy ?? skill_taxonomy,
      activity_taxonomy:
        nativeV3?.activity_taxonomy ?? DEFAULT_ACTIVITY_TAXONOMY_ID,
    },
    pause: skipCompensation || !tooltipFeature,
  });
  const compensationFetching =
    compensationStatus === DataProviderQueryStatus.Fetching;

  const distributionData = getDistributionDataFromPay(
    compensationData?.pay_v3?.entities ?? [],
    'total_compensation'
  );

  if (!tooltipFeature) {
    return null;
  }

  const { headcount: headcountFmt, headcountNumber } = (() => {
    const entity = compositionTooltipEntity;
    if (!entity) return { headcount: '-' };
    const headcounts = entity.metrics?.headcount;
    const lastHeadcount = headcounts?.[headcounts.length - 1];
    const headcount = lastHeadcount?.val;
    if (headcount === undefined) return { headcount: '-' };
    return { headcount: formatInt(headcount), headcountNumber: headcount };
  })();

  const growthRateFmt = (() => {
    const points = compositionTooltipEntity?.metrics?.growth_rate;
    const growth = points?.[points.length - 1]?.val;
    return typeof growth === 'number' ? formatSignedPct(growth) : '-';
  })();

  const {
    postings: postingFmt,
    postingsNumber,
    timeToFill: ttfFmt,
    timeToFillNumber,
  } = (() => {
    const lastValue = (
      points: ReadonlyArray<{ val: number }> | undefined | null
    ) => points?.[points.length - 1]?.val;

    const metrics = postingDataV3?.postings_v3?.entities?.[0]?.metrics;
    const activePostings = lastValue(metrics?.active);
    const timeToFill = lastValue(metrics?.time_to_fill);

    return {
      postings: activePostings ? formatInt(activePostings) : '-',
      postingsNumber: activePostings,
      timeToFill: timeToFill ? formatInt(timeToFill) : '-',
      timeToFillNumber: timeToFill,
    };
  })();

  const salaryData = compositionTooltipEntity?.metrics?.salary ?? [];

  const yearStartWage = salaryData[0]?.val ?? 0;
  const yearEndWage = salaryData[salaryData.length - 1]?.val ?? 0;
  const wageGrowth =
    yearStartWage !== 0 ? (yearEndWage - yearStartWage) / yearStartWage : 0;
  const marketTightness = calculateMarketTightnessMeterValue({
    wageGrowth,
    demandSupply: Number(postingsNumber) / Number(headcountNumber),
    timeToFill: Number(timeToFillNumber),
  });

  return {
    header:
      (tooltipFeature.label || tooltipFeature.location) ?? 'Unknown Geography',
    headcount: headcountFmt,
    growthRate: growthRateFmt,
    postings: postingFmt,
    timeToFill: ttfFmt,
    salaryDist: getPayPercentiles(distributionData, currencyFormatter),
    marketTightness: (marketTightness * 100).toString(),
    isLoading: compositionFetching || postingV3Fetching || compensationFetching,
    showSalaryData: !skipCompensation,
  };
};

const getTooltipCountyId = (
  tooltipFeature: GeoJsonProperties | null
): number | null => {
  if (
    !tooltipFeature ||
    !('id' in tooltipFeature) ||
    tooltipFeature['type'] !== TooltipFeatureType.COUNTY
  ) {
    return null;
  }
  const id = Number(tooltipFeature['id']);
  return Number.isNaN(id) ? null : id;
};

const getTooltipGeoFilter = (
  tooltipFeature: GeoJsonProperties | null,
  hasGranularSelection: boolean
):
  | (Pick<
      CompositionV2FullSnapshotReq['filters'],
      'country' | 'msa' | 'city' | 'state'
    > &
      Pick<PostingFilters, 'country' | 'msa' | 'city' | 'state'> & {
        county?: number[];
      })
  | null => {
  if (
    !tooltipFeature ||
    !('id' in tooltipFeature) ||
    !('type' in tooltipFeature)
  ) {
    return null;
  }
  const id = Number(tooltipFeature['id']);
  if (Number.isNaN(id)) return null;

  if (tooltipFeature['type'] === TooltipFeatureType.COUNTRY) {
    if (hasGranularSelection) return null;
    return { country: [id] };
  }
  if (tooltipFeature['type'] === TooltipFeatureType.METRO_AREA) {
    return { msa: [id] };
  }
  if (tooltipFeature['type'] === TooltipFeatureType.CITY) return { city: [id] };
  if (tooltipFeature['type'] === TooltipFeatureType.STATE) {
    return { state: [id] };
  }
  if (tooltipFeature['type'] === TooltipFeatureType.COUNTY) {
    return { county: [id] };
  }

  return null;
};

const hasGranularGeoSelection = (
  filters: CompositionV2Filters | PostingFilters | undefined | null
): boolean => {
  if (!filters) return false;

  const hasValues = (
    value: Array<number | string | null | undefined> | null | undefined
  ) => Array.isArray(value) && value.length > 0;

  const county = (filters as { county?: number[] | null }).county;

  return (
    hasValues(filters.msa) ||
    hasValues(filters.city) ||
    hasValues(filters.state) ||
    hasValues(county)
  );
};

const buildTooltipEntities = (
  tooltipFeature: GeoJsonProperties | null,
  fallbackEntities: CompositionV2Entity[]
): CompositionV2Entity[] => {
  if (!tooltipFeature) return fallbackEntities;
  const entId = Number(tooltipFeature['id']);
  if (Number.isNaN(entId)) return fallbackEntities;

  // County has no `PositionsEntityKind` — see `getTooltipCountyId`.
  const type = tooltipFeature['type'];
  if (type === TooltipFeatureType.COUNTRY) {
    return [{ EntID: entId, EntType: PositionsEntityKind.Country }];
  }
  if (type === TooltipFeatureType.METRO_AREA) {
    return [{ EntID: entId, EntType: PositionsEntityKind.Msa }];
  }
  if (type === TooltipFeatureType.CITY) {
    return [{ EntID: entId, EntType: PositionsEntityKind.City }];
  }
  if (type === TooltipFeatureType.STATE) {
    return [{ EntID: entId, EntType: PositionsEntityKind.State }];
  }

  return fallbackEntities;
};

const getPayPercentiles = (
  distributionData: DistributionData[],
  currencyFormatter: (value: number) => string
) => {
  if (!distributionData || distributionData.length === 0) return [] as string[];
  const first = distributionData[0];
  if (!first || !first.distribution || first.distribution.length === 0) {
    return [] as string[];
  }

  const salaries = first.distribution.map((d) => d.value);
  // Convert CDF ratios (0-1) to percentage scale (0-100) for easier comparison
  const cdf = first.distribution.map((d) => d.cdf * 100);

  const getPercentiles = ({
    salaries = [],
    cdf = [],
    percentiles,
  }: {
    salaries: number[];
    cdf: number[];
    percentiles: number[];
  }) => {
    return percentiles.map((percentile) => {
      let nearest = cdf[0];
      // @ts-expect-error noUncheckedIndexedAccess
      let smallestDiff = Math.abs(cdf[0] - percentile);
      for (let i = 1; i < cdf.length; i++) {
        // @ts-expect-error noUncheckedIndexedAccess
        const difference = Math.abs(cdf[i] - percentile);
        if (difference < smallestDiff) {
          smallestDiff = difference;
          nearest = cdf[i];
        }
      }
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      const cdfIndex = cdf.findIndex((item) => item === nearest);
      return cdfIndex === -1 ? 0 : salaries[cdfIndex] || 0;
    });
  };

  return getPercentiles({
    salaries,
    cdf,
    percentiles: [10, 50, 90],
  }).map((val) => currencyFormatter(val));
};
